/* FJC: merged products (one product with several colours/gemstones as variants).

   Shared logic + behaviour for:
   - Product page gallery: show only the selected colour's images (mobile Swiper
     and the desktop stacked gallery), re-applied after Eurus variant updates and
     after any gallery rebuild by the StarApps Variant Image embed.
   - Collection cards (Globo): mobile image swiper shows only the selected
     colour's images, rebuilt on swatch tap; card title swaps to the variant's
     display title (metafield custom.variant_display_title) when it is set;
     after a colour tap the desktop hover image stays within that colour and
     the card links are marked colour-only (?variant=…&fjc_colour_only=1) so
     the product page preselects the colour without treating Globo's size as
     chosen (both size pickers read that marker).
   - Collection ring drawer: real stock per size for the selected colour
     (used by the globoFilterRenderCompleted handler in layout/theme.liquid).

   Image grouping rule (matches every product checked on 2026-10-07): an image
   assigned to variants, plus the unassigned images that follow it up to the next
   assigned image, belong to those variants. Unassigned images before the first
   assigned image are shared and shown for every colour. A product with no
   variant-assigned images is left exactly as it is (no filtering at all).

   The pure functions on window.fjcMerged are unit tested in
   mydocs/notes/some_big_problem/tests (loaded with node:vm, so the tests run
   this shipped file). Keep them free of DOM access. */
(function (root) {
  'use strict';

  var SIZE_OPTION_RE = /^\s*(ring\s*)?size\s*$/i;
  var DEFAULT_COLOUR_NAMES = ['colour', 'color', 'gold colour', 'gold color', 'metal'];
  var PLACEHOLDER_SRCSET = /^data:image\/gif/i;

  /* ------------------------------------------------------------------ */
  /* Pure logic (no DOM)                                                  */
  /* ------------------------------------------------------------------ */

  function toIdString(id) {
    return id == null ? '' : String(id);
  }

  // images: [{ id, variantIds: [] }] in display order.
  // Returns { hasAssigned, shared: [imageId], groups: [{ variantIds: [], imageIds: [] }] }.
  function groupImages(images) {
    var shared = [];
    var groups = [];
    var current = null;
    var hasAssigned = false;
    (images || []).forEach(function (img) {
      var vids = (img.variantIds || []).map(toIdString).filter(Boolean);
      var id = toIdString(img.id);
      if (vids.length) {
        hasAssigned = true;
        current = { variantIds: vids, imageIds: [id] };
        groups.push(current);
      } else if (current) {
        current.imageIds.push(id);
      } else {
        shared.push(id);
      }
    });
    return { hasAssigned: hasAssigned, shared: shared, groups: groups };
  }

  // Image ids to show for the given variant ids, in original order, or null
  // when no filtering should happen (no assigned images, or no group matches).
  function imagesForVariants(grouping, variantIds, allImageIds) {
    if (!grouping || !grouping.hasAssigned) return null;
    var wanted = {};
    (variantIds || []).forEach(function (v) { wanted[toIdString(v)] = true; });
    var matched = {};
    var any = false;
    grouping.groups.forEach(function (g) {
      var hit = g.variantIds.some(function (v) { return wanted[v]; });
      if (hit) {
        any = true;
        g.imageIds.forEach(function (id) { matched[id] = true; });
      }
    });
    if (!any) return null;
    grouping.shared.forEach(function (id) { matched[id] = true; });
    var order = allImageIds || [].concat(grouping.shared, grouping.groups.reduce(function (acc, g) {
      return acc.concat(g.imageIds);
    }, []));
    return order.map(toIdString).filter(function (id) { return matched[id]; });
  }

  // Build the images[] shape from storefront-style data:
  //   media: [{ id }] in order; variants: [{ id, featured_media_id }]
  function imagesFromVariantFeaturedMedia(media, variants) {
    var byImage = {};
    (variants || []).forEach(function (v) {
      var mid = toIdString(v.featured_media_id);
      if (!mid) return;
      (byImage[mid] = byImage[mid] || []).push(toIdString(v.id));
    });
    return (media || []).map(function (m) {
      var id = toIdString(m.id);
      return { id: id, variantIds: byImage[id] || [] };
    });
  }

  // 0-based index of the size option among option names, or -1.
  function findSizeOptionIndex(optionNames) {
    var names = optionNames || [];
    for (var i = 0; i < names.length; i++) {
      var n = typeof names[i] === 'string' ? names[i] : (names[i] && names[i].name) || '';
      if (SIZE_OPTION_RE.test(n)) return i;
    }
    return -1;
  }

  // 0-based index of the colour option, or -1. colourNames: array or comma string.
  function findColourOptionIndex(optionNames, colourNames) {
    var list = colourNames;
    if (typeof list === 'string') list = list.split(',');
    list = (list && list.length ? list : DEFAULT_COLOUR_NAMES).map(function (s) {
      return String(s).trim().toLowerCase();
    });
    var names = optionNames || [];
    for (var i = 0; i < names.length; i++) {
      var n = typeof names[i] === 'string' ? names[i] : (names[i] && names[i].name) || '';
      if (list.indexOf(n.trim().toLowerCase()) !== -1) return i;
    }
    return -1;
  }

  // variants: [{ option1, option2, option3 }], position 1-based.
  function variantsWithOption(variants, position, value) {
    if (!position || value == null) return (variants || []).slice();
    var key = 'option' + position;
    return (variants || []).filter(function (v) { return v[key] === value; });
  }

  // 2026-10-20: the variant a collection card stands for. variantId = the card <select>'s
  // value (the template preselects it, Globo sets it on a swatch tap), so the title matches
  // the price, images and Add to Bag, as long as it has every selected swatch value
  // (selections: [{ position, value }], the tapped one first). Otherwise the first variant
  // with every selected value, else the first with the tapped value.
  function cardVariant(variants, variantId, selections) {
    var list = variants || [];
    var sels = (selections || []).filter(function (s) { return s && s.position && s.value != null; });
    var matches = function (v) {
      return sels.every(function (s) { return v['option' + s.position] === s.value; });
    };
    var id = variantId == null ? '' : toIdString(variantId);
    if (id) {
      for (var i = 0; i < list.length; i++) {
        if (toIdString(list[i].id) === id && matches(list[i])) return list[i];
      }
    }
    var all = list.filter(matches);
    if (all.length) return all[0];
    var first = sels.length ? variantsWithOption(list, sels[0].position, sels[0].value) : list;
    return first[0] || null;
  }

  // 2026-10-21: the variant a mobile swatch tap opens on the product page. tapped: { position,
  // value }; pinned: the card's other selected colour/gemstone swatches. Variants with the
  // tapped value AND every pinned value, else with the tapped value only; among them the first
  // in stock, then made to order (available, qty <= 0), then the first at all (a sold-out
  // colour still opens, on its sold-out variant). variants: [{ id, option1..3, available,
  // inventory_quantity }] (the card <select>'s options).
  function swatchTapVariant(variants, tapped, pinned) {
    if (!tapped || !tapped.position || tapped.value == null) return null;
    var withTapped = variantsWithOption(variants, tapped.position, tapped.value);
    var pins = (pinned || []).filter(function (p) { return p && p.position && p.position !== tapped.position && p.value != null; });
    var both = withTapped.filter(function (v) {
      return pins.every(function (p) { return v['option' + p.position] === p.value; });
    });
    var list = both.length ? both : withTapped;
    var tier = function (v) {
      var qty = parseInt(v.inventory_quantity, 10);
      if (!v.available) return 2;
      return qty > 0 ? 0 : 1;
    };
    for (var t = 0; t < 3; t++) {
      for (var i = 0; i < list.length; i++) {
        if (tier(list[i]) === t) return list[i];
      }
    }
    return null;
  }

  // 2026-10-22: the gemstone group of a metal + gemstone card for one metal. variants: as in
  // swatchTapVariant; metalPos/gemPos: 1-based option positions; gems: the gemstone values in
  // option order; current: the selected gemstone. Status per gemstone in that metal: 'stock'
  // (a variant with qty > 0), 'mto' (available), 'out' (exists, not available) or 'none' (no
  // such variant). pick = current when it exists in the metal, else the first gemstone in
  // stock, then made to order, then sold out (option order within each). shown = at most max
  // (3) gemstones in that same order, always including pick; more = the metal's other
  // gemstones (the "+N"). Matches the card template (snippets/globo.filter.product.liquid).
  function gemLayout(variants, metalPos, metal, gemPos, gems, current, max) {
    var limit = max || 3;
    var rank = { stock: 0, mto: 1, out: 2 };
    var status = {};
    (gems || []).forEach(function (g) { status[g] = 'none'; });
    (variants || []).forEach(function (v) {
      var g = v['option' + gemPos];
      if (v['option' + metalPos] !== metal || !status.hasOwnProperty(g)) return;
      var qty = parseInt(v.inventory_quantity, 10);
      var s = !v.available ? 'out' : (qty > 0 ? 'stock' : 'mto');
      if (status[g] === 'none' || rank[s] < rank[status[g]]) status[g] = s;
    });
    var ordered = [];
    ['stock', 'mto', 'out'].forEach(function (tier) {
      (gems || []).forEach(function (g) { if (status[g] === tier) ordered.push(g); });
    });
    var pick = current != null && status.hasOwnProperty(current) && status[current] !== 'none' ? current : (ordered[0] || null);
    var shown = ordered.slice(0, limit);
    if (pick != null && shown.indexOf(pick) === -1) {
      shown = shown.slice(0, limit - 1).concat(pick);
      shown.sort(function (a, b) { return ordered.indexOf(a) - ordered.indexOf(b); });
    }
    return { status: status, pick: pick, shown: shown, more: ordered.length - shown.length };
  }

  // Made to order = qty <= 0 and policy continue; qty <= 0 and deny = sold out.
  // Untracked variants (no inventory_management) count as in stock.
  function stockState(variant) {
    if (!variant) return 'sold_out';
    var qty = typeof variant.inventory_quantity === 'number' ? variant.inventory_quantity : parseInt(variant.inventory_quantity, 10);
    if (isNaN(qty)) qty = 0;
    var management = variant.inventory_management;
    if (management === null || management === undefined || management === '') return 'in_stock';
    if (qty > 0) return 'in_stock';
    var policy = String(variant.inventory_policy || '').toLowerCase();
    if (policy === 'continue') return 'made_to_order';
    return 'sold_out';
  }

  function stockLabel(state) {
    if (state === 'in_stock') return 'In stock';
    if (state === 'made_to_order') return '30 working days';
    return 'Sold out';
  }

  // Rows for the collection ring drawer. product: the fjc-stl-inventory JSON
  // ({ options: [names], variants: [...] }). colourPosition/colourValue: the
  // swatch selected on the card (1-based position), or null for all variants.
  // Returns [] when a colour was asked for but no variant has that value, so the
  // caller can fall back rather than list every colour's sizes unlabelled.
  function sizeDrawerRows(product, colourPosition, colourValue) {
    if (!product || !product.variants || !product.variants.length) return [];
    var sizeIndex = findSizeOptionIndex(product.options);
    var sizePosition = sizeIndex + 1;
    var matching = variantsWithOption(product.variants, colourPosition, colourValue);
    return matching.map(function (v) {
      var state = stockState(v);
      return {
        id: toIdString(v.id),
        size: (sizePosition > 0 && v['option' + sizePosition]) || v.title || '',
        state: state,
        label: stockLabel(state),
        inStock: state === 'in_stock',
        madeToOrder: state === 'made_to_order',
        soldOut: state === 'sold_out'
      };
    });
  }

  // Card price rule (mirrors the Liquid in snippets/globo.filter.product.liquid,
  // which Globo renders client side and cannot be unit tested directly):
  // the lowest non-zero price among purchasable variants; "From" when prices
  // differ; never 0 when any variant has a non-zero price.
  function cardPrice(variants) {
    var list = variants || [];
    var candidates = list.filter(function (v) { return Number(v.price) > 0 && v.available !== false; });
    if (!candidates.length) candidates = list.filter(function (v) { return Number(v.price) > 0; });
    if (!candidates.length) {
      return { price: list.length ? Number(list[0].price) || 0 : 0, from: false };
    }
    var min = Infinity, max = -Infinity;
    candidates.forEach(function (v) {
      var p = Number(v.price);
      if (p < min) min = p;
      if (p > max) max = p;
    });
    return { price: min, from: max > min };
  }

  // Display title for a variant: metafield value when set, else the product title.
  function displayTitle(variant, productTitle) {
    var t = variant && variant.display_title;
    if (typeof t === 'string' && t.trim() !== '') return t;
    return productTitle;
  }

  // Desktop card hover image for the selected colour (mirrors the Liquid in
  // snippets/globo.filter.product.liquid for the initial render): the first image
  // after the main one that belongs to the colour (its own group or a shared
  // image), else the first such image before it, else '' (nothing to hover to).
  // null = no grouping information (no assigned images, or none for these
  // variants, or the main image is not among them): leave the default alone.
  // 2026-11-02: photos shared by every variant (unassigned ones before the first assigned
  // photo) are no longer hover candidates: the hover is one of the shown variant's own
  // photos or nothing (Eima). The mobile slides still include them (imagesForVariants).
  function hoverImageId(grouping, variantIds, allImageIds, mainId) {
    var ids = imagesForVariants(grouping, variantIds, allImageIds);
    if (!ids) return null;
    ids = ids.filter(function (id) { return grouping.shared.indexOf(id) === -1; });
    var i = ids.indexOf(toIdString(mainId));
    if (i === -1) return null;
    if (i + 1 < ids.length) return ids[i + 1];
    return i > 0 ? ids[0] : '';
  }

  /* ------------------------------------------------------------------ */
  /* DOM behaviour                                                        */
  /* ------------------------------------------------------------------ */

  var api = {
    groupImages: groupImages,
    imagesForVariants: imagesForVariants,
    imagesFromVariantFeaturedMedia: imagesFromVariantFeaturedMedia,
    findSizeOptionIndex: findSizeOptionIndex,
    findColourOptionIndex: findColourOptionIndex,
    variantsWithOption: variantsWithOption,
    cardVariant: cardVariant,
    swatchTapVariant: swatchTapVariant,
    gemLayout: gemLayout,
    stockState: stockState,
    stockLabel: stockLabel,
    sizeDrawerRows: sizeDrawerRows,
    cardPrice: cardPrice,
    displayTitle: displayTitle,
    hoverImageId: hoverImageId
  };
  root.fjcMerged = api;

  if (typeof document === 'undefined') return;

  function parseJsonScript(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // Drop the 1px gif placeholder so the real `src` shows even if the lazy
  // loader never gets to this image (slides moved around by Swiper's loop).
  function ensureImageLoaded(img) {
    if (!img) return;
    var srcset = img.getAttribute('srcset');
    if (srcset && PLACEHOLDER_SRCSET.test(srcset)) img.removeAttribute('srcset');
  }

  /* ---------------- Product inventory + titles endpoint ---------------- */

  var productCache = {};

  // Same endpoint as the Shop The Look drawer and the collection ring drawer:
  // sections/fjc-stl-inventory.liquid, rendered via the Section Rendering API.
  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return productCache[handle];
    var rootPath = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
    var url = rootPath + 'products/' + encodeURIComponent(handle) + '?section_id=fjc-stl-inventory&_=' + Date.now();
    var promise = fetch(url, { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (body) {
        if (!body) return null;
        var first = body.indexOf('{');
        var last = body.lastIndexOf('}');
        if (first < 0 || last <= first) return null;
        try { return JSON.parse(body.slice(first, last + 1)); } catch (e) { return null; }
      })
      .catch(function () { return null; })
      .then(function (result) {
        if (result == null) delete productCache[handle];
        return result;
      });
    productCache[handle] = promise;
    return promise;
  }
  api.fetchProduct = fetchProduct;

  function handleFromCard(card) {
    var link = card.querySelector('a[href*="/products/"]');
    var href = link ? link.getAttribute('href') : '';
    var m = href.match(/\/products\/([^?\/#]+)/);
    return m ? m[1] : null;
  }
  api.handleFromCard = handleFromCard;

  /* ---------------- Product page gallery ---------------- */

  var pdpStates = {};

  function pdpState(sectionId) {
    var st = pdpStates[sectionId];
    if (st) return st;
    var dataEl = document.querySelector('script[data-fjc-variant-media="' + sectionId + '"]');
    var data = parseJsonScript(dataEl);
    if (!data || !data.media || !data.variants) return null;
    var images = imagesFromVariantFeaturedMedia(data.media, data.variants);
    st = {
      sectionId: sectionId,
      data: data,
      grouping: groupImages(images),
      allIds: images.map(function (i) { return i.id; }),
      slides: null,
      lastSwiperKey: null
    };
    pdpStates[sectionId] = st;
    return st;
  }

  function currentVariant(sectionId) {
    var el = document.querySelector('#variant-update-' + sectionId + ' script[data-selected-variant]');
    return parseJsonScript(el);
  }

  // Which variants the gallery should show: the resolved variant when there is
  // one, otherwise every variant with the colour currently selected in the picker.
  function pdpVariantIds(st) {
    var variant = currentVariant(st.sectionId);
    if (variant && variant.id) return [toIdString(variant.id)];
    var colourIndex = findColourOptionIndex(st.data.options, st.data.colour_option_names);
    if (colourIndex < 0) return null;
    var wrapper = document.querySelector('#variant-update-' + st.sectionId + ' [data-option-index="' + colourIndex + '"]');
    var value = null;
    if (wrapper) {
      var checked = wrapper.querySelector('input[type="radio"]:checked');
      if (checked) value = checked.value;
      var select = wrapper.querySelector('select');
      if (!value && select) value = select.value;
    }
    if (value == null) return null;
    return variantsWithOption(st.data.variants, colourIndex + 1, value).map(function (v) { return toIdString(v.id); });
  }

  function rebuildMobileSwiper(swiperEl) {
    if (!swiperEl) return;
    var existing = swiperEl.swiper;
    if (existing && typeof existing.destroy === 'function') existing.destroy(true, true);
    if (typeof root.Swiper !== 'function') return;
    if (typeof root.fjcInitProductSwiper === 'function') {
      root.fjcInitProductSwiper(swiperEl);
      return;
    }
    // Same options as the DOMContentLoaded init in layout/theme.liquid.
    new root.Swiper(swiperEl, {
      direction: 'horizontal',
      loop: true,
      pagination: { el: swiperEl.querySelector('.swiper-pagination'), clickable: true },
      speed: 400,
      touchRatio: 1,
      grabCursor: true
    });
  }

  // The slides captured from the mobile slider are still the ones it holds
  // (the other colours' slides are deliberately detached). If the wrapper now
  // contains anything we did not capture, someone rebuilt the slider.
  function sliderWasRebuilt(st, wrapper) {
    if (!st.slides) return false;
    var children = Array.prototype.slice.call(wrapper.children);
    return children.some(function (el) {
      return !st.slides.some(function (s) { return s.el === el; });
    });
  }

  function applyPdp(sectionId) {
    var st = pdpState(sectionId);
    if (!st || !st.grouping.hasAssigned) return;
    var container = document.getElementById('product-media-' + sectionId);
    if (!container) return;
    var ids = imagesForVariants(st.grouping, pdpVariantIds(st), st.allIds);
    var show = {};
    var key;
    if (ids) {
      ids.forEach(function (id) { show[id] = true; });
      key = ids.join(',');
    } else {
      key = '*';
    }

    // Mobile Swiper: slides are captured once, then the wrapper is rebuilt
    // with only the matching slides.
    var swiperEl = container.querySelector('.product-swiper');
    var wrapper = swiperEl ? swiperEl.querySelector('.swiper-wrapper') : null;
    if (wrapper) {
      if (sliderWasRebuilt(st, wrapper)) {
        st.slides = null;
        st.lastSwiperKey = null;
      }
      if (!st.slides) {
        st.slides = Array.prototype.slice.call(wrapper.querySelectorAll('.swiper-slide'))
          .filter(function (s) { return !s.classList.contains('swiper-slide-duplicate'); })
          .map(function (s) { return { el: s, id: s.getAttribute('data-fjc-media-id') || '' }; });
      }
      var swiperKey = key + '|' + st.slides.length;
      if (st.lastSwiperKey !== swiperKey) {
        st.lastSwiperKey = swiperKey;
        var keep = st.slides.filter(function (s) { return !ids || show[s.id]; });
        if (!keep.length) keep = st.slides.slice();
        // Rebuild only when the set of slides differs from what is shown
        // (Swiper's loop mode reorders slides, so order is not compared).
        var current = Array.prototype.slice.call(wrapper.children);
        var same = current.length === keep.length && keep.every(function (s) { return current.indexOf(s.el) !== -1; });
        var initialised = !!swiperEl.swiper;
        if (!same) {
          wrapper.innerHTML = '';
          keep.forEach(function (s) {
            s.el.classList.remove('swiper-slide-active', 'swiper-slide-next', 'swiper-slide-prev');
            s.el.removeAttribute('style');
            wrapper.appendChild(s.el);
          });
          if (initialised) rebuildMobileSwiper(swiperEl);
        }
        keep.forEach(function (s) { ensureImageLoaded(s.el.querySelector('img')); });
      }
    }

    // Desktop stacked gallery: hide the other colours' items with one style
    // rule keyed by media id. A rule (rather than classes on the items) keeps
    // working when StarApps clones the items from its own snapshot, and it
    // matches both the theme's "{section}-{mediaId}" and StarApps's bare
    // "{mediaId}" data-media-id values.
    var stacked = document.getElementById('stacked-' + sectionId);
    if (stacked) {
      var styleId = 'fjc-variant-media-style-' + sectionId;
      var styleEl = document.getElementById(styleId);
      if (!styleEl) {
        styleEl = document.createElement('style');
        styleEl.id = styleId;
        document.head.appendChild(styleEl);
      }
      var css = '';
      if (ids) {
        var selectors = [];
        st.allIds.forEach(function (id) {
          if (show[id]) return;
          selectors.push('#stacked-' + sectionId + ' .product-media-item[data-media-id="' + sectionId + '-' + id + '"]');
          selectors.push('#stacked-' + sectionId + ' .product-media-item[data-media-id="' + id + '"]');
        });
        if (selectors.length) css = selectors.join(',\n') + ' { display: none !important; }';
      }
      if (styleEl.textContent !== css) styleEl.textContent = css;
    }
  }

  function initPdp() {
    var dataEls = document.querySelectorAll('script[data-fjc-variant-media]');
    Array.prototype.forEach.call(dataEls, function (dataEl) {
      var sectionId = dataEl.getAttribute('data-fjc-variant-media');
      var st = pdpState(sectionId);
      if (!st || !st.grouping.hasAssigned) return;
      applyPdp(sectionId);

      var reapply = function () {
        setTimeout(function () { applyPdp(sectionId); }, 0);
        setTimeout(function () { applyPdp(sectionId); }, 60);
      };
      document.addEventListener('eurus:product-page-variant-select:updated:' + sectionId, reapply);
      // Colour radios/selects are re-rendered by Eurus; a delegated listener survives that.
      document.addEventListener('change', function (e) {
        var rootEl = document.getElementById('variant-update-' + sectionId);
        if (rootEl && e.target && rootEl.contains(e.target)) reapply();
      });

      // StarApps Variant Image (app embed) rebuilds the stacked gallery via
      // innerHTML a few seconds after load and on variant change; re-apply after
      // any outside change to the gallery DOM. The desktop rule is a style
      // element, so it already covers the new nodes; this mainly protects the
      // mobile slider if something else ever rebuilds it.
      var container = document.getElementById('product-media-' + sectionId);
      if (container && typeof MutationObserver === 'function') {
        var timer = null;
        new MutationObserver(function (mutations) {
          var relevant = mutations.some(function (m) {
            if (m.type !== 'childList' || !(m.addedNodes.length || m.removedNodes.length)) return false;
            // Swiper's own loop bookkeeping moves slides inside the wrapper; ignore that
            // (a rebuilt slider is detected inside applyPdp by its unknown slides).
            return !(m.target && m.target.classList && m.target.classList.contains('swiper-wrapper'));
          });
          if (!relevant) return;
          clearTimeout(timer);
          timer = setTimeout(function () { applyPdp(sectionId); }, 30);
        }).observe(container, { childList: true, subtree: true });
      }
    });
  }

  /* ---------------- Collection cards ---------------- */

  // 2026-10-29: a Metal + Initial card (one photo per variant) carries a hidden, selected swatch
  // for the letter it shows (snippets/globo.filter.product.liquid). Globo looks the variant up from
  // every selected swatch, so a metal tap keeps the letter (WG A to YG A).
  var LETTER_PIN = '.fjc-letter-pin .spf-product__swatch';

  function cardSwatchSelection(card, swatch) {
    var sw = swatch || card.querySelector('.spf-product__swatch.is-selected');
    if (!sw) return null;
    var position = parseInt(sw.getAttribute('data-option'), 10);
    var value = sw.getAttribute('data-option-value');
    if (!position || value == null) return null;
    return { position: position, value: value };
  }

  // 2026-10-19: a card with two colour groups (metal and gemstone, e.g. the two-stone claw drop
  // earrings) also keeps the selected swatch of the other group, so the slides and hover image
  // are those of the metal AND gemstone, not of every gemstone in the metal (26 slides instead
  // of 2). Size groups are not pinned. No variant with both values: the selection alone.
  // 2026-10-29: the hidden letter swatch of a Metal + Initial card (.fjc-letter-pin) is pinned
  // too, so WG A slides and hovers within WG A, not to WG B.
  function cardVariantIds(card, selection) {
    var select = card.querySelector('select.spf-product__variants');
    if (!select || !selection) return null;
    var pinned = [];
    Array.prototype.forEach.call(card.querySelectorAll('.spf-product__swatchs[data-fjc-colour-option="true"] .spf-product__swatch.is-selected, .spf-product__swatchs.fjc-swatchs--gem .spf-product__swatch.is-selected, ' + LETTER_PIN + '.is-selected'), function (sw) {
      var position = parseInt(sw.getAttribute('data-option'), 10);
      var value = sw.getAttribute('data-option-value');
      if (position && value != null && position !== selection.position) pinned.push({ position: position, value: value });
    });
    var ids = [];
    var narrowed = [];
    Array.prototype.forEach.call(select.options, function (opt) {
      if (opt.getAttribute('data-option' + selection.position) !== selection.value) return;
      ids.push(opt.value);
      var all = pinned.every(function (p) { return opt.getAttribute('data-option' + p.position) === p.value; });
      if (all) narrowed.push(opt.value);
    });
    return narrowed.length ? narrowed : ids;
  }

  // 2026-10-10: cards without colour swatches (for example the initial pendants, one
  // image per letter variant) have no selected swatch to filter by, so the slider showed
  // every image (23 slides and dots). Fall back to the variant Globo preselects in the
  // card's own <select> (the first available one, which is also the desktop card image).
  function cardSelectedVariantIds(card) {
    var select = card.querySelector('select.spf-product__variants');
    if (!select) return null;
    var opt = select.options[select.selectedIndex] || select.querySelector('option[selected]');
    return opt && opt.value ? [opt.value] : null;
  }

  function cardSlides(card) {
    var swiperEl = card.querySelector('.swiper.show-mobile');
    if (!swiperEl) return null;
    if (!card._fjcSlides) {
      var wrapper = swiperEl.querySelector('.swiper-wrapper');
      if (!wrapper) return null;
      card._fjcSlides = Array.prototype.slice.call(wrapper.querySelectorAll('.swiper-slide'))
        .filter(function (s) { return !s.classList.contains('swiper-slide-duplicate'); })
        .map(function (s, i) {
          var vids = (s.getAttribute('data-fjc-variants') || '').split(',').filter(Boolean);
          return { el: s, id: String(i), variantIds: vids };
        });
      card._fjcGrouping = groupImages(card._fjcSlides);
    }
    return { swiperEl: swiperEl, slides: card._fjcSlides, grouping: card._fjcGrouping };
  }

  // Filter the card's mobile slides for the selected swatch. Returns true when the
  // slide set changed. `reinit` rebuilds the Swiper instance (used after a swatch
  // tap; on first render the theme's own globoFilterRenderCompleted init follows).
  function applyCard(card, swatch, reinit) {
    var info = cardSlides(card);
    if (!info || !info.grouping.hasAssigned) return false;
    var selection = cardSwatchSelection(card, swatch);
    var ids = selection
      ? imagesForVariants(info.grouping, cardVariantIds(card, selection))
      : imagesForVariants(info.grouping, cardSelectedVariantIds(card));
    var keep = ids
      ? info.slides.filter(function (s) { return ids.indexOf(s.id) !== -1; })
      : info.slides.slice();
    if (!keep.length) keep = info.slides.slice();
    var wrapper = info.swiperEl.querySelector('.swiper-wrapper');
    var current = Array.prototype.slice.call(wrapper.children);
    // Same set of slides (order ignored: Swiper's loop mode reorders them).
    var same = current.length === keep.length && keep.every(function (s) { return current.indexOf(s.el) !== -1; });
    if (same && !reinit) {
      keep.forEach(function (s) { ensureImageLoaded(s.el.querySelector('img')); });
      return false;
    }
    var existing = info.swiperEl.swiper;
    if (existing && typeof existing.destroy === 'function') existing.destroy(true, true);
    wrapper.innerHTML = '';
    keep.forEach(function (s) {
      s.el.classList.remove('swiper-slide-active', 'swiper-slide-next', 'swiper-slide-prev');
      s.el.removeAttribute('style');
      wrapper.appendChild(s.el);
      ensureImageLoaded(s.el.querySelector('img'));
    });
    if (reinit && typeof root.Swiper === 'function') {
      new root.Swiper(info.swiperEl, {
        direction: 'horizontal',
        loop: true,
        pagination: { el: info.swiperEl.querySelector('.swiper-pagination'), clickable: true }
      });
    }
    return true;
  }

  // Called by layout/theme.liquid right before it initialises the card swipers.
  function prepareCards() {
    var cards = document.querySelectorAll('#gf-products .spf-product-card');
    Array.prototype.forEach.call(cards, function (card) {
      applyCard(card, null, false);
    });
  }
  api.prepareCards = prepareCards;

  // After the theme has created the card swipers: make sure the slide that is
  // shown has a real image even when the lazy loader missed it.
  function watchCardSwipers() {
    var swipers = document.querySelectorAll('#gf-products .swiper.show-mobile');
    Array.prototype.forEach.call(swipers, function (el) {
      var sw = el.swiper;
      if (!sw || el._fjcWatched === sw) return;
      el._fjcWatched = sw;
      var load = function () {
        var active = el.querySelector('.swiper-slide-active img') || el.querySelector('.swiper-slide img');
        ensureImageLoaded(active);
        var next = el.querySelector('.swiper-slide-next img');
        ensureImageLoaded(next);
      };
      try { sw.on('slideChange', load); sw.on('touchEnd', load); } catch (e) { /* ignore */ }
      load();
    });
  }

  /* ---------------- Desktop hover image and card links after a colour tap ---------------- */

  // Globo's swatch handler makes the tapped colour's image the main one and the next
  // image in product order the hover (secondary) one, which on a merged card is another
  // gemstone. Re-pick the hover image within the colour (same rule as the Liquid for
  // the initial render).
  function applyCardHover(card, selection) {
    var imgs = Array.prototype.slice.call(card.querySelectorAll('.show-desktop .spf-product-card__image'));
    if (imgs.length < 2 || !selection) return;
    var mainIndex = -1;
    var images = imgs.map(function (img, i) {
      if (mainIndex === -1 && img.classList.contains('spf-product-card__image-main')) mainIndex = i;
      return { id: String(i), variantIds: (img.getAttribute('data-variants') || '').split(',').filter(Boolean) };
    });
    if (mainIndex === -1) return;
    var grouping = groupImages(images);
    var allIds = images.map(function (im) { return im.id; });
    var id = hoverImageId(grouping, cardVariantIds(card, selection), allIds, String(mainIndex));
    if (id === null) return;
    // Globo's CSS hides the main image on hover, so the wrapper may only carry hover-effect
    // when there is a hover image to show (2026-10-13; same rule as the template).
    var wrapperEl = card.querySelector('.spf-product-card__image-wrapper');
    if (wrapperEl) wrapperEl.classList.toggle('hover-effect', id !== '');
    imgs.forEach(function (img, i) {
      if (i === mainIndex) return;
      if (String(i) === id) {
        img.classList.add('spf-product-card__image-secondary');
        img.classList.remove('spf-product-card__image-hidden');
        ensureImageLoaded(img);
      } else {
        img.classList.remove('spf-product-card__image-secondary');
        img.classList.add('spf-product-card__image-hidden');
      }
    });
  }

  // Globo rewrites every product link in the card to ?variant=<first available variant of
  // the tapped colour>. When the tapped swatch is a colour, metal or gemstone
  // (COLOUR_GROUPS), add fjc_colour_only=1 so the product page preselects the
  // colour but does not count the size inside that variant as chosen. A size swatch tap is
  // a real choice: since 2026-10-23 the template's links already carry the marker and Globo
  // keeps it (searchParams.set), so a size tap removes it.
  function markCardLinksColourOnly(card, swatch) {
    var group = swatch.closest('.spf-product__swatchs');
    if (!group) return;
    // metal and gemstone groups count as colour too (as on mobile, see COLOUR_GROUPS)
    var colour = !!swatch.closest(COLOUR_GROUPS);
    Array.prototype.forEach.call(card.querySelectorAll('a[href*="/products/"]'), function (link) {
      var href = link.getAttribute('href') || '';
      if (href.indexOf('variant=') === -1) return;
      if (colour === (href.indexOf('fjc_colour_only=') !== -1)) return;
      try {
        var url = new URL(href, root.location.href);
        if (!url.searchParams.has('variant')) return;
        if (colour) url.searchParams.set('fjc_colour_only', '1');
        else url.searchParams.delete('fjc_colour_only');
        link.setAttribute('href', url.toString());
      } catch (e) { /* leave the link as Globo wrote it */ }
    });
  }

  /* ---------------- Card titles ---------------- */

  function applyCardTitle(card, selection) {
    var link = card.querySelector('.spf-product-card__title a');
    if (!link) return;
    if (link._fjcDefaultTitle == null) link._fjcDefaultTitle = link.textContent.trim();
    var handle = handleFromCard(card);
    if (!handle) return;
    var token = (card._fjcTitleToken = (card._fjcTitleToken || 0) + 1);
    fetchProduct(handle).then(function (product) {
      if (!product || !product.variants || card._fjcTitleToken !== token) return;
      var hasTitles = product.variants.some(function (v) { return typeof v.display_title === 'string' && v.display_title.trim() !== ''; });
      if (!hasTitles) return;
      // 2026-10-20: every selected swatch counts, not only the tapped (or metal) one. A metal +
      // gemstone card took the first variant of its metal, so it read "Garnet" while it showed
      // the preselected Ruby, until a gemstone tap.
      var sel = selection || cardSwatchSelection(card, null);
      var selections = sel ? [sel] : [];
      Array.prototype.forEach.call(card.querySelectorAll('.spf-product__swatch.is-selected'), function (sw) {
        var s = cardSwatchSelection(card, sw);
        if (s && !selections.some(function (x) { return x.position === s.position; })) selections.push(s);
      });
      var ids = cardSelectedVariantIds(card);
      var variant = cardVariant(product.variants, ids ? ids[0] : null, selections);
      var title = displayTitle(variant, link._fjcDefaultTitle);
      if (link.textContent.trim() !== title) link.textContent = title;
    });
  }

  // 2026-10-13: a merged card is rendered with a colour already selected (the first
  // available one), so its title must be that colour's display title from the start, not
  // only after a swatch tap. Only cards whose colour option has a selected swatch are
  // fetched (one cached request per product); every other card keeps the product title.
  function applyDefaultCardTitles() {
    var cards = document.querySelectorAll('#gf-products .spf-product-card');
    Array.prototype.forEach.call(cards, function (card) {
      if (card._fjcTitleToken) return;
      var swatch = card.querySelector('[data-fjc-colour-option="true"] .spf-product__swatch.is-selected');
      if (!swatch) return;
      applyCardTitle(card, cardSwatchSelection(card, swatch));
    });
  }

  /* ---------------- Wiring ---------------- */

  function onCardsRendered() {
    prepareCards();
    watchCardSwipers();
    applyDefaultCardTitles();
  }

  function initCards() {
    // Globo renders into #gf-products and fires this after every render (the theme's own
    // handler, registered earlier, calls prepareCards() before creating the swipers).
    root.addEventListener('globoFilterRenderCompleted', function () { setTimeout(onCardsRendered, 0); });
    // Cards may already be on the page when this script runs.
    if (document.querySelector('#gf-products .spf-product-card')) {
      Array.prototype.forEach.call(document.querySelectorAll('#gf-products .spf-product-card'), function (card) {
        applyCard(card, null, !!(card.querySelector('.swiper.show-mobile') || {}).swiper);
      });
      watchCardSwipers();
      applyDefaultCardTitles();
    }
  }

  // 2026-10-21 (designer): below 1024px the colour and gemstone swatches on a card only show
  // the colours. A tap does not switch the card; it opens the product page on the tapped
  // colour, with the card's other shown colour/gemstone kept when that variant exists (see
  // swatchTapVariant). Runs on window in the capture phase, so Globo's swatch handler and the
  // card handlers below never see the tap. Size swatches keep their usual behaviour.
  var MOBILE_CARDS = '(max-width: 1023px)';
  var COLOUR_GROUPS = '.spf-product__swatchs[data-fjc-colour-option="true"], .spf-product__swatchs.fjc-swatchs--gem, .spf-product__swatchs.fjc-swatchs--metal';

  function cardSelectVariants(card) {
    var select = card.querySelector('select.spf-product__variants');
    if (!select) return [];
    return Array.prototype.map.call(select.options, function (o) {
      return {
        id: o.value,
        option1: o.getAttribute('data-option1'),
        option2: o.getAttribute('data-option2'),
        option3: o.getAttribute('data-option3'),
        available: o.getAttribute('data-available') === 'true',
        inventory_quantity: o.getAttribute('data-inventory-quantity')
      };
    });
  }

  function openSwatchOnPdp(e) {
    if (!root.matchMedia || !root.matchMedia(MOBILE_CARDS).matches) return;
    var swatch = e.target && e.target.closest ? e.target.closest('#gf-products .spf-product__swatch') : null;
    if (!swatch || !swatch.closest(COLOUR_GROUPS)) return;
    var card = swatch.closest('.spf-product-card');
    var link = card && card.querySelector('a[href*="/products/"]');
    var tapped = card && cardSwatchSelection(card, swatch);
    if (!link || !tapped) return;
    var pinned = [];
    Array.prototype.forEach.call(card.querySelectorAll('.spf-product__swatch.is-selected'), function (sw) {
      if (sw.closest(COLOUR_GROUPS)) pinned.push(cardSwatchSelection(card, sw));
    });
    var variants = cardSelectVariants(card);
    // 2026-10-22: a metal tap keeps the shown gemstone only when it exists in that metal
    var gemLaid = swatch.closest('.fjc-swatchs--metal') ? cardGemLayout(card, variants, tapped) : null;
    if (gemLaid && gemLaid.layout.pick != null) pinned = [{ position: gemLaid.gemPos, value: gemLaid.layout.pick }];
    // 2026-10-29: and the shown letter (else the first in stock in that metal, as swatchTapVariant does)
    var letter = card.querySelector(LETTER_PIN + '.is-selected');
    if (letter) pinned.push(cardSwatchSelection(card, letter));
    var variant = swatchTapVariant(variants, tapped, pinned);
    e.preventDefault();
    e.stopPropagation();
    try {
      var url = new URL(link.getAttribute('href'), root.location.href);
      url.search = '';
      if (variant) {
        url.searchParams.set('variant', variant.id);
        // a colour, not a size, was chosen (see markCardLinksColourOnly)
        url.searchParams.set('fjc_colour_only', '1');
      }
      root.location.href = url.toString();
    } catch (err) {
      root.location.href = link.href;
    }
  }
  root.addEventListener('click', openSwatchOnPdp, true);

  // 2026-10-22: a metal + gemstone card shows only the gemstones of its metal (template:
  // snippets/globo.filter.product.liquid, which renders every gemstone with data-fjc-gem-index
  // and hides the others). metal: { position, value } of the metal to lay out for.
  function cardGemLayout(card, variants, metal) {
    var group = card.querySelector('.spf-product__swatchs.fjc-swatchs--gem');
    var swatches = group ? Array.prototype.slice.call(group.querySelectorAll('.spf-product__swatch[data-fjc-gem-index]')) : [];
    if (!swatches.length || !metal) return null;
    swatches.sort(function (a, b) { return a.getAttribute('data-fjc-gem-index') - b.getAttribute('data-fjc-gem-index'); });
    var gemPos = parseInt(swatches[0].getAttribute('data-option'), 10);
    var current = group.querySelector('.spf-product__swatch.is-selected');
    var layout = gemLayout(variants, metal.position, metal.value, gemPos,
      swatches.map(function (sw) { return sw.getAttribute('data-option-value'); }),
      current ? current.getAttribute('data-option-value') : null, 3);
    return { group: group, swatches: swatches, gemPos: gemPos, layout: layout };
  }

  function applyCardGemLayout(laid) {
    var group = laid.group;
    var layout = laid.layout;
    var more = group.querySelector('.fjc-swatch-more');
    var byValue = {};
    laid.swatches.forEach(function (sw) { byValue[sw.getAttribute('data-option-value')] = sw; });
    laid.swatches.forEach(function (sw) {
      var value = sw.getAttribute('data-option-value');
      var shown = layout.shown.indexOf(value) !== -1;
      sw.classList.toggle('fjc-gem-off', !shown);
      sw.classList.toggle('soldout', layout.status[value] === 'out');
      sw.classList.toggle('is-selected', value === layout.pick);
    });
    // shown gemstones first, in layout order, then the hidden ones, then "+N"
    layout.shown.concat(laid.swatches.map(function (sw) { return sw.getAttribute('data-option-value'); })
      .filter(function (v) { return layout.shown.indexOf(v) === -1; }))
      .forEach(function (v) { group.insertBefore(byValue[v], more || null); });
    if (more) {
      more.textContent = '+' + layout.more;
      more.setAttribute('aria-label', layout.more + ' more gemstones');
      more.classList.toggle('fjc-gem-off', layout.more <= 0);
    }
  }

  // Desktop (1024px and wider): a metal tap re-lays the gemstone group before Globo's own
  // swatch handler runs (capture phase), so Globo switches the card to that metal AND the
  // gemstone selected here (it looks up the variant with every .is-selected swatch).
  root.addEventListener('click', function (e) {
    if (root.matchMedia && root.matchMedia(MOBILE_CARDS).matches) return;
    var swatch = e.target && e.target.closest ? e.target.closest('#gf-products .fjc-swatchs--metal .spf-product__swatch') : null;
    var card = swatch && swatch.closest('.spf-product-card');
    if (!card) return;
    var laid = cardGemLayout(card, cardSelectVariants(card), cardSwatchSelection(card, swatch));
    if (laid) applyCardGemLayout(laid);
  }, true);

  // 2026-10-29: before Globo's swatch handler, a tapped metal without the card's letter lets go
  // of the letter pin, so Globo picks that metal's first available variant (instead of finding
  // none and disabling Add to Bag); syncLetterPin then pins the letter Globo picked.
  root.addEventListener('click', function (e) {
    if (root.matchMedia && root.matchMedia(MOBILE_CARDS).matches) return;
    var swatch = e.target && e.target.closest ? e.target.closest('#gf-products .spf-product__swatch') : null;
    var card = swatch && swatch.closest('.spf-product-card');
    var pin = card && card.querySelector(LETTER_PIN + '.is-selected');
    var tapped = pin && cardSwatchSelection(card, swatch);
    if (!tapped) return;
    var group = swatch.closest('.spf-product__swatchs');
    var wanted = [tapped];
    Array.prototype.forEach.call(card.querySelectorAll('.spf-product__swatch.is-selected'), function (sw) {
      if (sw.closest('.spf-product__swatchs') === group && group) return;
      var s = cardSwatchSelection(card, sw);
      if (s && s.position !== tapped.position) wanted.push(s);
    });
    var exists = cardSelectVariants(card).some(function (v) {
      return wanted.every(function (s) { return v['option' + s.position] === s.value; });
    });
    if (!exists) pin.classList.remove('is-selected');
  }, true);

  // After Globo's swatch handler: the letter pin follows the variant Globo selected.
  function syncLetterPin(card) {
    var pin = card.querySelector(LETTER_PIN);
    var select = card.querySelector('select.spf-product__variants');
    var opt = select && select.options[select.selectedIndex];
    if (!pin || !opt) return;
    var value = opt.getAttribute('data-option' + pin.getAttribute('data-option'));
    if (value != null) pin.setAttribute('data-option-value', value);
    pin.classList.add('is-selected');
  }

  // 2026-10-22: product page buy box: a metal radio carries data-fjc-gem-jump (the option value
  // id of the gemstone to select with that metal, snippets/product-variant-options.liquid) when
  // the selected gemstone does not exist in it. Select that gemstone before Eurus reads the
  // checked values (its change handler waits a tick), so the page asks for metal + that
  // gemstone in one request.
  document.addEventListener('change', function (e) {
    var input = e.target;
    var id = input && input.getAttribute && input.getAttribute('data-fjc-gem-jump');
    if (!id || !input.checked) return;
    var scope = input.closest('.variant-selects-main') || input.closest('form') || document;
    var gem = scope.querySelector('input[data-option-value-id="' + id + '"]');
    if (gem) gem.checked = true;
  }, true);

  document.addEventListener('click', function (e) {
    var swatch = e.target && e.target.closest ? e.target.closest('#gf-products .spf-product__swatch') : null;
    if (!swatch) return;
    var card = swatch.closest('.spf-product-card');
    if (!card) return;
    var selection = cardSwatchSelection(card, swatch);
    // Globo updates the card in the same click; run after it.
    setTimeout(function () {
      syncLetterPin(card);
      applyCard(card, swatch, true);
      applyCardHover(card, selection);
      markCardLinksColourOnly(card, swatch);
      // A chosen colour has one price: drop the "From" prefix Globo leaves behind.
      var from = card.querySelector('.fjc-price-from');
      if (from && from.parentNode) from.parentNode.removeChild(from);
      applyCardTitle(card, selection);
    }, 0);
  });

  function init() {
    initPdp();
    initCards();
  }

  // This script is deferred: the DOM is parsed, and on product pages the first
  // applyPdp() runs before the theme's DOMContentLoaded Swiper init, so that
  // init already sees only the selected colour's slides.
  init();
})(typeof window !== 'undefined' ? window : globalThis);
